import React, { useEffect, useState } from 'react';
import { Trophy, FileCheck2, ShieldCheck, Download, Printer, TrendingUp } from 'lucide-react';
import type { ChampionHomeData, DashboardTrend } from '../../../types/aai';
import { aaiApi } from '../../../services/aaiApi';
import { Card, Hero, MonoLabel, Bar, BandPill, RiskPill, StatusPill, ApprovalQueue } from './ui';
import { exportChampionCsv, downloadAdoptionReportPdf } from './dashboardExport';

// Compact adoption-velocity sparkline (P2) from the weekly trend points.
const TrendSpark: React.FC<{ trend: DashboardTrend }> = ({ trend }) => {
  if (trend.suppressed || trend.points.length < 2) return null;
  const vals = trend.points.map((p) => p.maturity_pct);
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const w = 132, h = 34;
  const pts = trend.points
    .map((p, i) => `${(i / (trend.points.length - 1)) * w},${h - ((p.maturity_pct - min) / span) * (h - 6) - 3}`)
    .join(' ');
  const last = vals[vals.length - 1], first = vals[0];
  return (
    <div className="flex items-center gap-2" title={`${trend.points.length} weeks of measured maturity`}>
      <svg width={w} height={h} className="overflow-visible">
        <polyline points={pts} fill="none" stroke="#4080f2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className={`text-[11px] font-semibold ${last >= first ? 'text-emerald-600' : 'text-amber-600'}`}>
        {last >= first ? '↑' : '↓'} {first}→{last}%
      </span>
    </div>
  );
};

const ChampionHome: React.FC<{ data: ChampionHomeData; onRefresh: () => void }> = ({ data, onRefresh }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [trend, setTrend] = useState<DashboardTrend | null>(null);
  useEffect(() => { aaiApi.getDashboardTrend().then((r) => setTrend(r.data)).catch(() => setTrend(null)); }, []);
  const decide = async (id: string, decision: string, comment?: string) => {
    setBusy(id);
    try { await aaiApi.decideRequest(id, { decision, comment }); window.dispatchEvent(new Event('aai:approvals-changed')); onRefresh(); }
    finally { setBusy(null); }
  };
  const maturityLabel = data.maturity.suppressed ? '—' : `${data.maturity.maturity_pct ?? '—'}%`;

  return (
  <div className="space-y-4">
    {/* maturity headline + adoption velocity + export */}
    <Card>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-5">
          <div>
            <MonoLabel className="mb-1">Company maturity</MonoLabel>
            <div className="flex items-baseline gap-2">
              <span className="text-[30px] font-bold tracking-tight">{maturityLabel}</span>
              {data.maturity.band && <BandPill band={data.maturity.band} />}
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5">Configured scoring formula · {data.enrolled} enrolled</p>
          </div>
          {trend && !trend.suppressed && (
            <div className="pl-5 border-l border-gray-100">
              <MonoLabel className="mb-1.5 flex items-center gap-1"><TrendingUp size={12} /> Velocity</MonoLabel>
              <TrendSpark trend={trend} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportChampionCsv(data)}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-600 hover:text-brand-700 border border-gray-200 rounded-lg px-3 py-1.5 whitespace-nowrap"><Download size={14} /> CSV</button>
          <button onClick={() => { void downloadAdoptionReportPdf(); }}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-3 py-1.5 whitespace-nowrap"><Printer size={14} /> Board report</button>
        </div>
      </div>
      {data.maturity.suppressed && (
        <p className="text-[11.5px] text-gray-400 mt-2">Maturity appears once at least {data.min_group_size} people have onboarded (privacy threshold).</p>
      )}
    </Card>

    {/* company readiness bars */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
      {data.company_bars.slice(0, 3).map((c) => (
        <Card key={c.label}>
          <div className="flex justify-between items-baseline mb-2.5">
            <MonoLabel>{c.label}</MonoLabel>
            <span className="text-[22px] font-bold tracking-tight">{c.val}</span>
          </div>
          <Bar pct={c.pct} color="brand" height="h-2.5" />
          <p className="text-[11.5px] text-gray-400 mt-2">{c.note}</p>
        </Card>
      ))}
      {data.company_bars.length === 0 && (
        <div className="col-span-full"><Card><p className="text-[13px] text-gray-500">Company readiness appears once enough people have onboarded (privacy threshold {data.min_group_size}).</p></Card></div>
      )}
    </div>

    {/* dept maturity + spotlights */}
    <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-4">
      <div className="bg-white border border-gray-100 rounded-xl p-5">
        <div className="font-bold text-sm mb-3.5">Department maturity</div>
        {data.departments.map((d) => (
          <div key={d.department} className="flex items-center gap-3 py-2.5 border-t border-gray-50 first:border-0">
            <div className="w-28 font-semibold text-[13px] truncate">{d.department}</div>
            <div className="flex-1"><Bar pct={d.pct} color="brand" track="bg-gray-100" height="h-1.5" /></div>
            <div className="min-w-[92px] text-right"><BandPill band={d.band} /></div>
          </div>
        ))}
        {data.departments.length === 0 && <p className="text-[13px] text-gray-400">No department data yet.</p>}
      </div>

      <Hero>
        <div className="flex items-center gap-2 mb-3">
          <Trophy size={17} className="text-accent-500" />
          <span className="font-bold text-sm">Spotlights</span>
          <span className="text-[10.5px] text-white/55 ml-auto">Recognition, never ranking</span>
        </div>
        {data.spotlights.map((sp) => (
          <div key={sp.who} className="py-2.5 border-t border-white/10 first:border-0">
            <div className="text-[13px] font-semibold">{sp.who}</div>
            <div className="text-[12px] text-white/65 leading-snug">{sp.what}</div>
          </div>
        ))}
        {data.spotlights.length === 0 && <p className="text-[12.5px] text-white/55">Spotlights appear as people progress.</p>}
      </Hero>
    </div>

    {/* gate 2 — requests a dept head forwarded (plus deptless requests), awaiting final sign-off */}
    <ApprovalQueue items={data.approvals} onDecide={decide} busyId={busy} title="Awaiting your approval" />

    {/* use-case registry */}
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
        <div><span className="font-bold text-sm">Use-case registry</span><span className="text-xs text-gray-400 ml-2">Approved requests · examiner-ready inventory</span></div>
        <span className="font-sans text-[10.5px] text-purple-600 bg-purple-50 px-2.5 py-0.5 rounded-full font-semibold">NIST AI RMF</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead><tr className="text-left bg-gray-50/70 text-gray-400">
            {['Use case', 'Dept', 'Risk', 'Model', 'Human-in-loop', 'Status'].map((h) => (
              <th key={h} className="px-4 py-2.5 font-sans text-[10.5px] uppercase tracking-wider font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.registry.map((g, i) => (
              <tr key={i} className="border-t border-gray-50">
                <td className="px-4 py-2.5 font-semibold">{g.name}</td>
                <td className="px-4 py-2.5 text-gray-600">{g.department}</td>
                <td className="px-4 py-2.5"><RiskPill risk={g.risk} /></td>
                <td className="px-4 py-2.5 font-sans text-[11.5px] text-gray-600">{g.model || '— unset'}</td>
                <td className="px-4 py-2.5 text-gray-600">{g.human_in_loop || '— unset'}</td>
                <td className="px-4 py-2.5"><StatusPill status={g.status} /></td>
              </tr>
            ))}
            {data.registry.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[13px] text-gray-400">No approved use cases yet — they appear here as requests are approved.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>

    {/* audit gating banner */}
    <Hero className="flex items-center gap-3 !py-3.5">
      <span className="w-9 h-9 shrink-0 rounded-lg bg-accent-500/20 text-accent-500 grid place-items-center"><ShieldCheck size={19} /></span>
      <div className="flex-1">
        <div className="font-bold text-[13.5px]">Opening an individual is logged.</div>
        <div className="text-[12.5px] text-white/65">As champion you may drill into one person's readiness — the platform writes an audit-log entry (who, when, who was viewed) before the data returns. Coaching, not surveillance.</div>
      </div>
      <FileCheck2 size={18} className="text-white/40 shrink-0" />
    </Hero>
  </div>
  );
};

export default ChampionHome;
